import { useCallback, useRef, useState } from "react";
import { UploadCloud, Loader2, Trash2 } from "lucide-react";
import { adminUploadImage, imageUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop image upload.
 * Props:
 *   value: current image_id (string | null)
 *   onChange: (newImageId | null) => void
 *   label: shown above
 *   aspect: tailwind aspect class, e.g. "aspect-[16/9]"
 */
export const ImageUpload = ({ value, onChange, label, aspect = "aspect-[4/3]" }) => {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const upload = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Vennligst velg en bildefil.");
        return;
      }
      setBusy(true);
      setProgress(0);
      try {
        const res = await adminUploadImage(file, setProgress);
        onChange(res.data.id);
        toast.success("Bilde lastet opp");
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.detail || "Opplasting feilet");
      } finally {
        setBusy(false);
        setProgress(0);
      }
    },
    [onChange],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const url = value ? imageUrl(value) : null;

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#68919B]">{label}</p>
      )}
      <div
        data-testid="image-upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative ${aspect} w-full border-2 border-dashed cursor-pointer transition-colors flex items-center justify-center overflow-hidden bg-[#F3F5E7] ${
          drag
            ? "border-[#68919B] bg-[#EAEDDC]"
            : "border-[#68919B]/40 hover:border-[#68919B]"
        }`}
      >
        {url && !busy && (
          <img
            src={url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {(drag || !url) && !busy && (
          <div className="relative z-10 text-center px-6 py-8 bg-[#F3F5E7]/85">
            <UploadCloud
              size={28}
              strokeWidth={1.3}
              className="mx-auto text-[#68919B] mb-3"
            />
            <p className="text-sm text-[#3D5E68] font-light">
              Dra og slipp bilde her, eller klikk for å velge
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#68919B]/70 mt-2">
              JPG · PNG · WEBP · maks 10 MB
            </p>
          </div>
        )}
        {busy && (
          <div className="relative z-10 text-center bg-[#F3F5E7]/90 px-6 py-6">
            <Loader2
              size={26}
              className="mx-auto text-[#68919B] mb-3 animate-spin"
              strokeWidth={1.4}
            />
            <p className="text-sm text-[#3D5E68]">Laster opp… {progress}%</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
      {value && !busy && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
          className="text-[10px] uppercase tracking-[0.25em] text-[#68919B] hover:text-[#9E0232] flex items-center gap-1.5"
        >
          <Trash2 size={12} /> Fjern bilde
        </button>
      )}
    </div>
  );
};
